"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TermPlanningGrid = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * TermPlanningGrid component - drag-and-drop grid for planning terms and courses
 */
const react_1 = require("react");
const core_1 = require("@dnd-kit/core");
const sortable_1 = require("@dnd-kit/sortable");
const utilities_1 = require("@dnd-kit/utilities");
const course_1 = require("../models/course");
const Select_1 = require("./Select");
const Button_1 = require("./Button");
require("./TermPlanningGrid.scss");
/**
 * Sortable term row component
 */
const SortableTermRow = ({ termPlan, index, availableCourses, onAddCourse, onRemoveCourse, onToggleFailed, onRemoveTerm, disabled, }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = (0, sortable_1.useSortable)({
        id: termPlan.id,
    });
    const [selectedCourseId, setSelectedCourseId] = (0, react_1.useState)('');
    const style = {
        transform: utilities_1.CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const handleAddCourse = () => {
        if (selectedCourseId) {
            onAddCourse(termPlan.id, selectedCourseId);
            setSelectedCourseId('');
        }
    };
    const courseOptions = availableCourses
        .filter((course) => !termPlan.courses.some((c) => c.course.id === course.id))
        .map((course) => ({
        value: course.id,
        label: course_1.CourseHelpers.getDisplayLabel(course),
    }));
    return ((0, jsx_runtime_1.jsxs)("div", { ref: setNodeRef, style: style, className: "term-planning-grid__term-row", children: [(0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__term-header", children: [(0, jsx_runtime_1.jsx)("button", Object.assign({ type: "button", className: "term-planning-grid__drag-handle" }, attributes, listeners, { disabled: disabled, "aria-label": `Drag to reorder ${termPlan.term.name}`, children: "\u22EE\u22EE" })), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__term-info", children: [(0, jsx_runtime_1.jsxs)("span", { className: "term-planning-grid__term-position", children: ["Term ", index + 1] }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__term-name", children: termPlan.term.name })] }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__remove-term", onClick: () => onRemoveTerm(termPlan.id), disabled: disabled, "aria-label": `Remove ${termPlan.term.name}`, children: "\u00D7" })] }), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__courses", children: [termPlan.courses.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "term-planning-grid__no-courses", children: "No courses added" })) : ((0, jsx_runtime_1.jsx)("ul", { className: "term-planning-grid__course-list", children: termPlan.courses.map((coursePlan) => {
                            const isLOA = course_1.CourseHelpers.isLOA(coursePlan.course);
                            const isElective = course_1.CourseHelpers.isElective(coursePlan.course);
                            const isInactive = course_1.CourseHelpers.isInactive(coursePlan.course);
                            const isFailed = coursePlan.failed;
                            const courseClasses = [
                                'term-planning-grid__course-item',
                                isLOA && 'term-planning-grid__course-item--loa',
                                isElective && 'term-planning-grid__course-item--elective',
                                isInactive && 'term-planning-grid__course-item--inactive',
                                isFailed && 'term-planning-grid__course-item--failed',
                            ].filter(Boolean).join(' ');
                            return ((0, jsx_runtime_1.jsxs)("li", { className: courseClasses, children: [(0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-code", children: coursePlan.course.code }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-name", children: coursePlan.course.name }), coursePlan.course.credits > 0 && ((0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-credits", children: coursePlan.course.credits })), !isLOA && onToggleFailed && ((0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__toggle-failed", onClick: () => onToggleFailed(termPlan.id, coursePlan.id), disabled: disabled, "aria-label": isFailed ? `Mark ${coursePlan.course.name} as not failed` : `Mark ${coursePlan.course.name} as failed`, title: isFailed ? 'Mark as not failed' : 'Mark as failed', children: "\u270B" })), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__remove-course", onClick: () => onRemoveCourse(termPlan.id, coursePlan.id), disabled: disabled, "aria-label": `Remove ${coursePlan.course.name}`, children: "\u00D7" })] }, coursePlan.id));
                        }) })), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__add-course", children: [(0, jsx_runtime_1.jsx)(Select_1.Select, { id: `course-select-${termPlan.id}`, label: "Add Course", value: selectedCourseId, onChange: (e) => setSelectedCourseId(e.target.value), options: courseOptions, disabled: disabled || courseOptions.length === 0, placeholder: courseOptions.length === 0 ? 'No courses available' : 'Select a course' }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleAddCourse, disabled: disabled || !selectedCourseId, size: "small", variant: "secondary", children: "Add" })] })] })] }));
};
/**
 * TermPlanningGrid component for planning degree plan terms and courses
 * @param props - TermPlanningGridProps
 * @returns TermPlanningGrid component
 */
const TermPlanningGrid = ({ termPlans, availableTerms, availableCourses, onChange, onToggleFailed, disabled = false, }) => {
    const [selectedTermId, setSelectedTermId] = (0, react_1.useState)('');
    const sensors = (0, core_1.useSensors)((0, core_1.useSensor)(core_1.PointerSensor), (0, core_1.useSensor)(core_1.KeyboardSensor, {
        coordinateGetter: sortable_1.sortableKeyboardCoordinates,
    }));
    const handleDragEnd = (0, react_1.useCallback)((event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = termPlans.findIndex((tp) => tp.id === active.id);
            const newIndex = termPlans.findIndex((tp) => tp.id === over.id);
            const reordered = (0, sortable_1.arrayMove)(termPlans, oldIndex, newIndex).map((tp, idx) => (Object.assign(Object.assign({}, tp), { position: idx + 1 })));
            onChange(reordered);
        }
    }, [termPlans, onChange]);
    const handleAddTerm = (0, react_1.useCallback)(() => {
        if (!selectedTermId)
            return;
        const term = availableTerms.find((t) => t.id === selectedTermId);
        if (!term)
            return;
        const newTermPlan = {
            id: `term-plan-${Date.now()}`,
            term,
            position: termPlans.length + 1,
            courses: [],
        };
        onChange([...termPlans, newTermPlan]);
        setSelectedTermId('');
    }, [selectedTermId, availableTerms, termPlans, onChange]);
    const handleAddCourse = (0, react_1.useCallback)((termId, courseId) => {
        const course = availableCourses.find((c) => c.id === courseId);
        if (!course)
            return;
        const updated = termPlans.map((tp) => {
            if (tp.id === termId) {
                return Object.assign(Object.assign({}, tp), { courses: [
                        ...tp.courses,
                        {
                            id: `course-plan-${Date.now()}-${courseId}`,
                            course,
                            failed: false,
                        },
                    ] });
            }
            return tp;
        });
        onChange(updated);
    }, [availableCourses, termPlans, onChange]);
    const handleRemoveCourse = (0, react_1.useCallback)((termId, courseId) => {
        const updated = termPlans.map((tp) => {
            if (tp.id === termId) {
                return Object.assign(Object.assign({}, tp), { courses: tp.courses.filter((c) => c.id !== courseId) });
            }
            return tp;
        });
        onChange(updated);
    }, [termPlans, onChange]);
    const handleRemoveTerm = (0, react_1.useCallback)((termId) => {
        const updated = termPlans
            .filter((tp) => tp.id !== termId)
            .map((tp, idx) => (Object.assign(Object.assign({}, tp), { position: idx + 1 })));
        onChange(updated);
    }, [termPlans, onChange]);
    // Non-special courses already placed in any term — excluded globally from all dropdowns.
    // Failed courses are excluded from this set so they can be re-added to a future term.
    const usedNonSpecialIds = (0, react_1.useMemo)(() => {
        const ids = new Set();
        termPlans.forEach((tp) => tp.courses.forEach((c) => {
            if (!course_1.CourseHelpers.isSpecial(c.course) && !c.failed)
                ids.add(c.course.id);
        }));
        return ids;
    }, [termPlans]);
    // Approved courses sorted: special courses first, then alphabetically by displayCode
    const filteredCourses = (0, react_1.useMemo)(() => {
        return availableCourses
            .filter((c) => c.approved !== false && !usedNonSpecialIds.has(c.id))
            .sort((a, b) => {
            const aSpecial = course_1.CourseHelpers.isSpecial(a);
            const bSpecial = course_1.CourseHelpers.isSpecial(b);
            if (aSpecial !== bSpecial)
                return aSpecial ? -1 : 1;
            const aCode = a.displayCode || a.code;
            const bCode = b.displayCode || b.code;
            return aCode.localeCompare(bCode);
        });
    }, [availableCourses, usedNonSpecialIds]);
    // Terms sorted chronologically, excluding already-added terms
    const termOptions = (0, react_1.useMemo)(() => availableTerms
        .filter((term) => !termPlans.some((tp) => tp.term.id === term.id))
        .sort((a, b) => {
        const diff = a.startDate.getTime() - b.startDate.getTime();
        return diff !== 0 ? diff : a.endDate.getTime() - b.endDate.getTime();
    })
        .map((term) => ({
        value: term.id,
        label: term.name,
    })), [availableTerms, termPlans]);
    return ((0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid", children: [(0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__header", children: [(0, jsx_runtime_1.jsx)("h3", { className: "term-planning-grid__title", children: "Term Planning" }), (0, jsx_runtime_1.jsx)("p", { className: "term-planning-grid__description", children: "Add terms and assign courses to each term" })] }), (0, jsx_runtime_1.jsx)(core_1.DndContext, { sensors: sensors, collisionDetection: core_1.closestCenter, onDragEnd: handleDragEnd, children: (0, jsx_runtime_1.jsx)(sortable_1.SortableContext, { items: termPlans.map((tp) => tp.id), strategy: sortable_1.verticalListSortingStrategy, children: (0, jsx_runtime_1.jsx)("div", { className: "term-planning-grid__terms", children: termPlans.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "term-planning-grid__empty", children: (0, jsx_runtime_1.jsx)("p", { children: "No terms added yet. Add a term to get started." }) })) : (termPlans.map((termPlan, index) => ((0, jsx_runtime_1.jsx)(SortableTermRow, { termPlan: termPlan, index: index, availableCourses: filteredCourses, onAddCourse: handleAddCourse, onRemoveCourse: handleRemoveCourse, onToggleFailed: onToggleFailed, onRemoveTerm: handleRemoveTerm, disabled: disabled }, termPlan.id)))) }) }) }), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__add-term", children: [(0, jsx_runtime_1.jsx)(Select_1.Select, { id: "term-select", label: "Add Term", value: selectedTermId, onChange: (e) => setSelectedTermId(e.target.value), options: termOptions, disabled: disabled || termOptions.length === 0, placeholder: termOptions.length === 0 ? 'No terms available' : 'Select a term' }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleAddTerm, disabled: disabled || !selectedTermId, variant: "secondary", children: "Add Term" })] })] }));
};
exports.TermPlanningGrid = TermPlanningGrid;
exports.default = exports.TermPlanningGrid;
